import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { SchemaOrg, organizationSchema } from '../../utils/seo';

const companyInfo = [
  { label: '会社名', value: 'おかやま保険株式会社' },
  { label: '代表取締役', value: '岡山太郎' },
  { label: '所在地', value: '〒700-0000 岡山市北区○○町1-2-3' },
  { label: '電話番号', value: '086-012-3456' },
  { label: 'FAX', value: '086-012-3457' },
  { label: '設立', value: '2020年' },
  { label: '従業員数', value: '3名' },
  { label: '営業時間', value: '9:00〜18:00' },
  { label: '定休日', value: '日曜日・祝日' },
  { label: '加入団体', value: '一般社団法人日本損害保険代理業協会\n生命保険協会' },
  { label: '取扱保険会社', value: '主要生損保12社' },
  { label: '事業内容', value: '損害保険代理業\n生命保険募集業\n保険コンサルティング業' },
];

export default function AboutPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '会社概要 | おかやま保険株式会社',
    description: 'おかやま保険株式会社の会社概要。岡山市北区に本社を置き、地域密着型の保険代理店として2020年に設立。',
    url: `${siteUrl}/about`,
    mainEntity: organizationSchema,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'ホーム',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '会社概要',
        item: `${siteUrl}/about`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <SchemaOrg type="WebPage" data={aboutPageSchema} />
      <SchemaOrg type="WebPage" data={breadcrumbSchema} />
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative flex items-center justify-center overflow-hidden" style={{ height: '40vh' }}>
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/5677a360c18991bf477a951a9cf8f960.jpg"
              alt="オフィスビル"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>
          </div>
          
          <div className="relative z-10 text-center text-white px-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 md:mb-4">会社概要</h1>
            <nav className="text-sm">
              <Link to="/" className="hover:text-[#FF9B7A] transition-colors cursor-pointer">ホーム</Link>
              <span className="mx-2">&gt;</span>
              <span>会社概要</span>
            </nav>
          </div>
        </section>

        {/* Company Info Table */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-6">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              {companyInfo.map((info, index) => (
                <div key={index} className={`grid grid-cols-1 md:grid-cols-3 ${index !== companyInfo.length - 1 ? 'border-b border-gray-100' : ''}`}>
                  <div className="bg-gray-50 px-6 md:px-8 py-4 md:py-6 font-bold text-[#2C3E50] text-sm md:text-base md:border-r border-gray-100">
                    {info.label}
                  </div>
                  <div className="md:col-span-2 px-6 md:px-8 py-4 md:py-6 text-sm md:text-base text-gray-700 whitespace-pre-line leading-relaxed">
                    {info.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CEO Message */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-12 items-center">
              <div className="md:col-span-2">
                <div className="rounded-2xl overflow-hidden h-96 md:h-auto">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20businessman%20CEO%20in%20his%2050s%20wearing%20business%20suit%2C%20confident%20friendly%20smile%2C%20sitting%20in%20modern%20office%2C%20natural%20lighting%2C%20authentic%20Japanese%20executive%2C%20simple%20clean%20background&width=600&height=800&seq=ceo-portrait-001&orientation=portrait"
                    alt="代表取締役 岡山太郎"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="md:col-span-3">
                <p className="text-gray-500 text-xs md:text-sm mb-2 md:mb-4">代表挨拶</p>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2C3E50] mb-4 md:mb-8 leading-tight">
                  地域の皆さまと共に歩む<br />
                  保険代理店
                </h2>
                
                <div className="space-y-4 md:space-y-6 text-sm md:text-base text-gray-700 leading-relaxed">
                  <p>
                    岡山で生まれ育った私たちだからこそ、この地域の気候や生活スタイル、地域特有のリスクを深く理解しています。
                    お客様一人ひとりのライフステージに寄り添い、真に必要な保険をご提案することを心がけております。
                  </p>
                  
                  <p>
                    保険は「もしもの時」のためのものですが、私たちはお客様の「今」を大切にし、現在の家計に無理のない範囲で、
                    将来への安心をお届けしたいと考えています。
                  </p>
                  
                  <p>
                    創業から5年、多くのお客様に支えられ、3,000世帯を超える皆さまにご利用いただけるまでになりました。
                    これからも地域密着型の保険代理店として、岡山市・倉敷市・総社市の皆さまの暮らしを守り続けてまいります。
                  </p>
                  
                  <p>
                    保険に関するお悩みや疑問がございましたら、いつでもお気軽にご相談ください。
                    お客様の笑顔が、私たちの何よりの喜びです。
                  </p>
                </div>
                
                <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-gray-200">
                  <p className="font-bold text-[#2C3E50] text-base md:text-lg">
                    代表取締役　岡山太郎
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Access Map */}
        <section className="py-16 md:py-24 bg-[#FAF8F3]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-4xl font-bold text-[#2C3E50] mb-3 md:mb-4">
                アクセス
              </h2>
              <p className="text-gray-600 text-sm md:text-lg">
                お気軽にお越しください
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                <div className="h-80">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3281.3929141167604!2d133.9350437!3d34.6617241!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDM5JzQyLjEiTiAxMzPCsDU2JzA2LjIiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="おかやま保険株式会社の所在地"
                  ></iframe>
                </div>
              </div>

              <div className="space-y-6 md:space-y-8">
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#2C3E50] mb-4 md:mb-6">
                    所在地・お問い合わせ
                  </h3>
                  <div className="space-y-4 md:space-y-5">
                    <div className="flex items-start gap-3">
                      <i className="ri-map-pin-line text-lg md:text-xl text-[#FF9B7A] mt-1"></i>
                      <div>
                        <p className="font-medium text-[#2C3E50] mb-1 text-sm md:text-base">住所</p>
                        <p className="text-gray-600 text-sm md:text-base leading-relaxed">〒700-0000<br />岡山市北区○○町1-2-3</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-phone-line text-lg md:text-xl text-[#FF9B7A] mt-1"></i>
                      <div>
                        <p className="font-medium text-[#2C3E50] mb-1 text-sm md:text-base">電話番号</p>
                        <a href="tel:0860123456" className="text-gray-600 text-sm md:text-base hover:text-[#FF9B7A] transition-colors cursor-pointer">
                          086-012-3456
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-time-line text-lg md:text-xl text-[#FF9B7A] mt-1"></i>
                      <div>
                        <p className="font-medium text-[#2C3E50] mb-1 text-sm md:text-base">営業時間</p>
                        <p className="text-gray-600 text-sm md:text-base leading-relaxed">9:00〜18:00<br />定休日：日曜日・祝日</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-train-line text-lg md:text-xl text-[#FF9B7A] mt-1"></i>
                      <div>
                        <p className="font-medium text-[#2C3E50] mb-1 text-sm md:text-base">アクセス</p>
                        <p className="text-gray-600 text-sm md:text-base leading-relaxed">JR岡山駅より徒歩10分<br />岡山電鉄「県庁通り駅」より徒歩5分</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-car-line text-lg md:text-xl text-[#FF9B7A] mt-1"></i>
                      <div>
                        <p className="font-medium text-[#2C3E50] mb-1 text-sm md:text-base">駐車場</p>
                        <p className="text-gray-600 text-sm md:text-base leading-relaxed">専用駐車場3台完備<br />近隣にコインパーキングあり</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-6 md:pt-8 border-t border-gray-200">
                  <Link
                    to="/contact"
                    className="inline-block bg-[#FF9B7A] text-white px-8 py-4 rounded-full font-bold hover:bg-[#FF8A65] transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base"
                  >
                    お問い合わせ
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
